import { ScoreOnlyView, ScoreView } from '@/components/game-play';
import GameError from '@/components/game-play/GameError';
import GameMapView from '@/components/game-play/GameMap';
import { HoleInformation } from '@/components/game-play/HoleInformation';
import type { PlayerInfo, ScoreData } from '@/components/game-play/types';
import type { GolfGameLiveData } from '@/plugins/live-activity';
import { api } from '@/utils/api';
import { mediumImpact } from '@/utils/capacitorUtils';
import { liveActivityService } from '@/utils/liveActivityService';
import { supabase } from '@/utils/supabase';
import { widgetService, type GameWidgetData } from '@/utils/widgetService';
import { Capacitor } from '@capacitor/core';
import {
  IonAlert,
  IonButton,
  IonContent,
  IonIcon,
  IonModal,
  IonPage,
  IonToast,
  useIonRouter
} from '@ionic/react';
import { document, golf } from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const PlayGame: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const { t } = useTranslation();
  const ionRouter = useIonRouter();
  const [activeHole, setActiveHole] = useState(1);
  const modal = useRef<HTMLIonModalElement>(null);
  const [showLeaveAlert, setShowLeaveAlert] = useState(false);
  // We'll use the modal ref instead of action sheet
  const [showToast, setShowToast] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [teeId, setTeeId] = useState<string>('');

  // Game data state
  const [liveActivityId, setLiveActivityId] = useState<string | null>(null);
  const [courseName, setCourseName] = useState('');
  const [courseLayout, setCourseLayout] = useState('');
  const [currentPlayer, setCurrentPlayer] = useState<PlayerInfo | null>(null);
  const [players, setPlayers] = useState<PlayerInfo[]>([]);
  const [gameType, setGameType] = useState<string>('strokeplay'); // Default to strokeplay
  const [ownerId, setOwnerId] = useState<string | null>(null); // Store the game owner ID
  const [hasGpsData, setHasGpsData] = useState(false);
  const [scoreModalOpen, setScoreModalOpen] = useState(false);

  // Use refs to track active subscription and data loading to prevent duplicates
  const subscriptionRef = useRef<any>(null);
  const dataLoadedRef = useRef<boolean>(false);
  const scoreModal = useRef<HTMLIonModalElement>(null);
  const scoreViewRef = useRef<HTMLIonModalElement>(null);
  const pageRef = useRef(null);
  // State to control HoleInformation visibility
  const [showHoleInfoModal, setShowHoleInfoModal] = useState(true);

  // View state (map or score)
  const [currentView, setCurrentView] = useState<'map' | 'score' | 'scoreOnly'>('map');
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [playerScores, setPlayerScores] = useState<Record<string, Record<number, ScoreData>>>({});
  const [holeData, setHoleData] = useState<Array<{ hole: number, par: number, distance: number, handicap?: number, name?: string }>>([]);
  const [gameError, setGameError] = useState<{ status: number; message: string } | null>(null);

  const navigateToHole = (hole: number) => {
    if (hole < 1 || hole > holeData.length) return;
    setActiveHole(hole);

    updateGameWidgetAndLiveActivity(playerScores[currentPlayer?.id || '']?.[hole]?.score || 0, hole);
  };

  const goToNextHole = () => {
    if (activeHole < holeData.length) {
      mediumImpact();
      navigateToHole(activeHole + 1);
    }
  };

  const goToPreviousHole = () => {
    if (activeHole > 1) {
      mediumImpact();
      navigateToHole(activeHole - 1);
    }
  };

  // Toggle between different views
  const goToView = (view: 'map' | 'score' | 'scoreOnly') => {
    mediumImpact();
    setCurrentView(view);

    // Hide HoleInformation modal when showing score view
    if (view === 'score') {
      setShowHoleInfoModal(false);
    } else if (view === 'map') {
      // Show HoleInformation modal when returning to map view
      setShowHoleInfoModal(true);
    }
  };

  const toggleView = () => {
    mediumImpact();
    if (hasGpsData) {
      // In GPS mode, toggle between map and score views
      setCurrentView(currentView === 'map' ? 'score' : 'map');
    }
  };

  // Open score modal for a specific player
  const openScoreModal = (playerId: string) => {
    const playerToScore = players.find(p => p.id === playerId);
    if (playerToScore) {
      setSelectedPlayerId(playerId);
      setScoreModalOpen(true);
      // Hide HoleInformation modal when opening score modal
      setShowHoleInfoModal(false);
    }
  };

  // Handle score submission
  const handleSaveScore = (scoreData: ScoreData, playerId: string) => {
    mediumImpact();

    // Update the player scores state - this will be updated by the real-time subscription too,
    // but we update it immediately for a better user experience
    setPlayerScores(prev => {
      const playerScoreMap = prev[playerId] || {};
      return {
        ...prev,
        [playerId]: {
          ...playerScoreMap,
          [activeHole]: scoreData
        }
      };
    });

    // Update the widget with current game data (on iOS only)
    updateGameWidgetAndLiveActivity(scoreData.score);

    setScoreModalOpen(false);
    // Only dismiss the score modal if we're coming from the ScoreView component
    // For ScoreOnlyView, we want to keep the sheet open
    if (currentView !== 'scoreOnly') {
      // Only go back to map view if we're not in scoreOnly mode
      if (activeHole < holeData.length) {
        navigateToHole(activeHole + 1);
      }
      goToView('map');
    } else {
      // Show HoleInformation modal again when in scoreOnly mode
      setShowHoleInfoModal(true);
    }
  };
  // Handle skipping a hole
  const handleSkipHole = () => {
    mediumImpact();
    if (activeHole < holeData.length) {
      navigateToHole(activeHole + 1);
    }
    goToView('map');
  };

  const handleQuitGame = () => {
    if (modal.current) {
      modal.current.dismiss();
    }
    // Mark the game as seen in localStorage to prevent resume modal from showing immediately
    if (gameId) {
      localStorage.setItem(`game_resume_${gameId}`, 'true');
    }
    // Don't clear widget data or end Live Activity when quitting - we want to keep them active
    ionRouter.push('/dashboard');
  };

  const handleFinishGame = () => {
    if (modal.current) {
      modal.current.dismiss();
    }
    // Mark the game as seen in localStorage to prevent resume modal from showing immediately
    if (gameId) {
      localStorage.setItem(`game_resume_${gameId}`, 'true');
    }
    // Don't clear widget data or end Live Activity when quitting - we want to keep them active
    ionRouter.push('/game/' + gameId + '/result');
  };

  // Delete game with confirmation
  const handleDeleteGame = () => {
    setShowLeaveAlert(true);
  };

  // Confirm and delete the game
  const confirmDeleteGame = async () => {
    try {
      if (modal.current) {
        modal.current.dismiss();
      }

      if (gameId) {
        // Delete the game and all associated data
        await api.delete(`/game/${gameId}`);
        setLocationError(null);
        setShowToast(true);

        // Clear widget data when game is deleted
        await widgetService.clearGameWidget();

        // End Live Activity when game is deleted
        if (liveActivityId) {
          await liveActivityService.endLiveActivity(liveActivityId);
          setLiveActivityId(null);
        }

        // Navigate back to dashboard
        ionRouter.push('/dashboard');
      }
    } catch (error) {
      console.error('Error deleting game:', error);
      setLocationError('Failed to delete game');
      setShowToast(true);
    }
  };

  const updateGameWidgetAndLiveActivity = async (
    currentScore: number,
    hole: number = activeHole
  ) => {
    if (!gameId || Capacitor.getPlatform() !== 'ios') return;

    const currentHole = holeData[hole - 1];
    if (!currentHole) return;

    // Widget data (for widgetService)
    const widgetData: GameWidgetData = {
      courseName: courseName,
      courseLayout: courseLayout,
      currentHole: hole,
      totalHoles: holeData.length,
      par: currentHole.par,
      hcp: currentPlayer?.handicap,
      holeName: `Trou ${hole}`,
      score: currentScore,
      isActive: true
    };

    widgetService.updateGameWidget(widgetData);

    // Live Activity data
    const liveData: GolfGameLiveData = {
      gameName: gameId,
      playerName: currentPlayer?.name || 'Player',
      currentHole: hole,
      totalHoles: holeData.length,
      par: currentHole.par,
      hcp: currentPlayer?.handicap,
      holeName: `Trou ${hole}`,
      score: currentScore,
      courseName: courseName,
      courseLayout: courseLayout
    };

    if (liveActivityId) {
      console.log('Updating existing Live Activity with:', liveData);
      await liveActivityService.updateLiveActivity(liveData, liveActivityId);
    } else {
      const newActivityId = await liveActivityService.startLiveActivity(liveData);
      if (newActivityId) {
        console.log('Live Activity started with ID:', newActivityId);
        setLiveActivityId(newActivityId);
      } else {
        console.warn('Failed to start Live Activity - ID was null');
      }
    }
  };

  // Load game data from API
  const loadGameData = async () => {
    if (gameId && !isLoading && !dataLoadedRef.current) {
      setIsLoading(true);
      setGameError(null);
      try {
        const response = await api.get(`/game/${gameId}`);
        const gameData = response.data;
        // Mark as loaded to prevent duplicate calls
        dataLoadedRef.current = true;

        // Check if the game data is empty or invalid
        if (!gameData || !gameData.game) {
          setGameError({
            status: 404,
            message: t('game_not_found')
          });
        }

        // Set course info
        if (gameData.game.name) setCourseName(gameData.game.name);
        if (gameData.game.courseLayout) setCourseLayout(gameData.game.courseLayout);
        setHasGpsData(gameData.gps === true);

        // Set game type (defaults to strokeplay if not provided)
        if (gameData.game.type) setGameType(gameData.game.type);

        // Set the game owner ID
        if (gameData.game.ownerId) setOwnerId(gameData.game.ownerId);

        // Set player info from the first player (current user)
        if (gameData.players && gameData.players.length > 0) {
          const firstPlayer = gameData.players[0];
          setCurrentPlayer({
            id: firstPlayer.id,
            name: firstPlayer.name || 'Player',
            avatar: firstPlayer.avatar,
            userId: firstPlayer.userId
          });
          setPlayers(gameData.players.map((p: { id: string; name: string; avatar?: string, userId?: string }) => ({
            id: p.id,
            name: p.name,
            avatar: p.avatar,
            userId: p.userId
          })));
        }

        // Set hole data from the holes returned by the API
        // Now using holesByTee structure from the API
        const currentTeeId = gameData.players && gameData.players.length > 0 ? gameData.players[0].teeId : null;
        if (currentTeeId) {
          setTeeId(currentTeeId);
        }
        if (currentTeeId && gameData.holesByTee && gameData.holesByTee[currentTeeId]) {
          // Get all holes from the API
          const allHoles = gameData.holesByTee[currentTeeId].map((h: { holeNumber: number; par?: number; distance?: number, handicap?: number }) => ({
            hole: h.holeNumber,
            handicap: h.handicap,
            par: h.par || 4,
            distance: h.distance || 0,
            name: `Hole ${h.holeNumber}`,
            geodata: gameData.gpsData[h.holeNumber] || []
          }));

          // Filter holes based on the game setup holes value
          // 18 = all 18 holes
          // 0 = front 9 (holes 1-9)
          // 9 = back 9 (holes 10-18)
          let filteredHoles;
          const holesValue = gameData.game.holes;

          if (holesValue === 0) {
            // Front 9 (holes 1-9)
            filteredHoles = allHoles.filter((h: { hole: number }) => h.hole >= 1 && h.hole <= 9);
          } else if (holesValue === 9) {
            // Back 9 (holes 10-18)
            filteredHoles = allHoles.filter((h: { hole: number }) => h.hole >= 10 && h.hole <= 18);
          } else {
            // All 18 holes or any other value
            filteredHoles = allHoles;
          }

          setHoleData(filteredHoles);

          if (Capacitor.isNativePlatform()) {
            updateGameWidgetAndLiveActivity(playerScores[currentPlayer?.id || '']?.[activeHole]?.score);
          }
        } else {
          console.error('Hole data not found', gameData);
          setGameError({
            status: 404,
            message: t('hole_data_not_found')
          });
          return;
        }

        // Load scores from Supabase
        loadScoresFromSupabase();

        // Set GPS data availability and adjust view accordingly
        setCurrentView(gameData.gps === true ? 'map' : 'scoreOnly');
        updateGameWidgetAndLiveActivity(playerScores[currentPlayer?.id || '']?.[activeHole]?.score || 0);
      } catch (error: any) {
        console.error('Error loading game data:', error);

        // Handle different error types based on status code
        const status = error.response?.status || 500;
        let message = '';

        if (status === 404 || status === 403) {
          message = error.response?.data?.message || t('game_not_found');
        } else {
          message = error.response?.data?.message || t('error_loading_game');
        }

        setGameError({ status, message });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const [scoresLoading, setScoresLoading] = useState(true);

  // Load scores from Supabase
  const loadScoresFromSupabase = async () => {
    try {
      setScoresLoading(true);
      if (gameId) {
        const { data, error } = await supabase
          .from('game_scores')
          .select('*')
          .eq('game_id', gameId);

        if (error) {
          throw error;
        }

        // Process the scores into the format we need
        const newPlayerScores: Record<string, Record<number, ScoreData>> = {};

        data.forEach((score: any) => {
          const { player_id, hole_number, score: scoreValue, putts, water_penalties, ob_penalties, drop_penalties, fairway_hit } = score;

          // Initialize player's score object if it doesn't exist
          if (!newPlayerScores[player_id]) {
            newPlayerScores[player_id] = {};
          }

          // Add the score data for this hole
          newPlayerScores[player_id][hole_number] = {
            score: scoreValue,
            putts: putts,
            waterPenalties: water_penalties,
            obPenalties: ob_penalties,
            dropPenalties: drop_penalties,
            fairwayHit: fairway_hit
          };
        });

        // Update the playerScores state with the loaded data
        setPlayerScores(newPlayerScores);
      }
    } catch (error) {
      console.error('Error in loadScoresFromSupabase:', error);
    } finally {
      setScoresLoading(false);
    }
  };

  // Handle browser beforeunload event
  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = '';
  };

  useEffect(() => {
    if (!gameId) return;

    // Add beforeunload event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // If we already have an active subscription, don't create another one
    if (!subscriptionRef.current) {
      subscriptionRef.current = supabase
        .channel(`game_scores_${gameId}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'game_scores',
          filter: `game_id=eq.${gameId}`
        }, () => {
          loadScoresFromSupabase();
        })
        .subscribe();
    }

    // Load game data from API (function has internal deduping)
    loadGameData();

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Clean up subscription
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [gameId]);

  return (
    <IonPage ref={pageRef}>
      {isLoading ? (
        ""
      ) : gameError ? (
        <GameError
          status={gameError?.status}
          message={gameError?.message}
          onBack={() => ionRouter.push('/dashboard')}
        />
      ) : (
        <IonContent scrollY={false} className="ion-padding-0">
          {/* Toast for location errors */}
          <IonToast
            isOpen={showToast}
            onDidDismiss={() => setShowToast(false)}
            message={locationError || 'An error occurred'}
            duration={3000}
            position="top"
            color="danger"
          />

          {/* Swipeable container */}
          <div
            className="h-full w-full"
            onClick={async () => {
              if (modal?.current) {
                const currentBreakpoint = await modal.current.getCurrentBreakpoint();
                if (currentBreakpoint && currentBreakpoint > 0.22) {
                  modal.current.setCurrentBreakpoint(0.22);
                }
              }
            }}
          >
            {currentView === 'map' && (
              <GameMapView
                activeHole={activeHole}
                holeData={holeData}
                navigateToHole={navigateToHole}
                goToScoreView={() => goToView('score')}
                handleLeaveGame={() => modal.current?.present()}
                players={players}
              />
            )}

            {currentView === 'score' && (
              <ScoreView
                gameId={gameId}
                teeId={teeId}
                holeNumber={activeHole}
                par={holeData[activeHole - 1]?.par}
                players={players}
                initialActivePlayer={currentPlayer}
                onSaveScore={handleSaveScore}
                onSkipHole={handleSkipHole}
                onClose={() => goToView('map')}
                initialScoreData={playerScores}
              />
            )}

            {currentView === 'scoreOnly' && (
              <>
                {scoresLoading ? (
                  ""
                ) : (
                  <ScoreOnlyView
                    activeHole={activeHole}
                    par={holeData[activeHole - 1]?.par || 4}
                    holeData={holeData}
                    players={players}
                    currentPlayer={currentPlayer}
                    navigateToHole={navigateToHole}
                    handleLeaveGame={() => modal.current?.present()}
                    onSkipHole={handleSkipHole}
                    gameId={gameId}
                    hasGps={hasGpsData}
                    teeId={teeId}
                    initialScoreData={playerScores}
                    openScoreModal={openScoreModal}
                    onSwitchToMapView={() => goToView('map')}
                    clubName={courseName}
                    courseName={courseLayout}
                    gameType={gameType}
                    ownerId={ownerId}
                    pageRef={pageRef.current}
                  />
                )}
              </>
            )}
          </div>

          {/* Toggle button for GPS mode */}
          {hasGpsData && (
            <div className="fixed bottom-20 right-4 z-10">
              <IonButton
                onClick={toggleView}
                className="rounded-full shadow-lg"
                fill="solid"
                color="primary"
              >
                <IonIcon icon={currentView === 'map' ? document : golf} slot="icon-only" />
              </IonButton>
            </div>
          )}

          {/* Score Modal for non-GPS mode */}
          <IonModal
            ref={scoreModal}
            isOpen={scoreModalOpen}
            onWillDismiss={async (e) => {
              // If modal is being dismissed by a swipe or backdrop click, save the score first
              if (e.detail.role === 'backdrop' || e.detail.role === 'gesture') {
                // Save the score using the ref
                if (scoreViewRef.current) {
                  try {
                    await scoreViewRef.current.saveScore();
                  } catch (error) {
                    console.error('Error auto-saving score:', error);
                  }
                }
              }
              setScoreModalOpen(false);
              // Show HoleInformation modal again when score modal is dismissed
              setShowHoleInfoModal(true);
            }}
            className="auto-height"
          >
            <ScoreView
              ref={scoreViewRef}
              sheetMode
              gameId={gameId}
              teeId={teeId}
              holeNumber={activeHole}
              par={holeData[activeHole - 1]?.par}
              players={players}
              initialActivePlayer={selectedPlayerId ? players.find(player => player.id === selectedPlayerId)! : currentPlayer}
              onSaveScore={handleSaveScore}
              onSkipHole={handleSkipHole}
              onClose={() => setScoreModalOpen(false)}
              initialScoreData={playerScores}
            />
          </IonModal>
          {/* Hole information */}
          <IonModal
            ref={modal}
            isOpen={showHoleInfoModal}
            initialBreakpoint={0.22}
            breakpoints={[0.22, 1]}
            backdropDismiss={false}
            handleBehavior="cycle"
            showBackdrop={true}
            backdropBreakpoint={1}
            className="auto-height w-full z-0"
            style={{ '--border-color': 'var(--ion-border-color)', '--border-width': '1px', '--border-style': 'solid' }}
          >
            <HoleInformation
              activeHole={activeHole}
              holeData={holeData}
              gameId={gameId}
              players={players}
              onQuitGame={handleQuitGame}
              onDeleteGame={handleDeleteGame}
              onFinishGame={handleFinishGame}
              nextHole={goToNextHole}
              previousHole={goToPreviousHole}
            />
          </IonModal>

          {/* Leave game confirmation alert */}
          <IonAlert
            isOpen={showLeaveAlert}
            onDidDismiss={() => setShowLeaveAlert(false)}
            header={t('delete_game')}
            message={t('delete_game_confirmation')}
            buttons={[
              {
                text: t('cancel'),
                role: 'cancel',
              },
              {
                text: t('delete'),
                role: 'destructive',
                handler: confirmDeleteGame,
              },
            ]}
          />
        </IonContent>
      )}
    </IonPage>
  );
};

export default PlayGame;
